import { useCallback, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useAuthContext } from "../LauncherAuthContext";
import axios, { AxiosResponse } from "axios";
import { JWTDecoded, LoginOutput, UserAccess } from "../types";
import { jwtDecode } from "jwt-decode";
import { message } from "antd";

export const useLoginButton = (callback: () => void, redirect?: string) => {
    const [working, setWorking] = useState<boolean>(false);
    const { executeRecaptcha } = useGoogleReCaptcha();
    const { t } = useTranslation();
    const { authenticateUser, setUserAccess } = useAuthContext();
    const navigate = useNavigate();


    const login = useCallback((jwt: string) => {
        if (working || !executeRecaptcha) {
            return Promise.resolve({
                success: false,
                account: undefined
            });
        } else {
            return executeRecaptcha('login').then(captcha => {
                setWorking(true);
                return axios.post<object, AxiosResponse<LoginOutput>>(`/api/login`, {
                    captcha,
                    jwt,
                    decoded: jwtDecode(jwt) as JWTDecoded
                })
                    .then(r => r.data)
                    .catch(() => {
                        setWorking(false);
                        return message.error(t('auth.failure')).then(() => {
                            return Promise.reject({
                                success: false,
                                account: undefined
                            });
                        });
                    });
            });
        }
    }, [working, executeRecaptcha]);

    const captureUser = useCallback((result: LoginOutput, jwt: JWTDecoded) => {
        authenticateUser(result.account!, jwt);
        axios.get<object, AxiosResponse<UserAccess>>(`/api/launcher/users/account/${result!.account?.id}/access`)
            .then(r => r.data)
            .then(setUserAccess)
            .then(() => {
                setWorking(false);

                if (result.success && result.account?.status === 'VERIFIED') {
                    message.success(t('auth.success'), 0.3).then(() => {
                        callback();
                    });
                }
                if (result.success && result.account?.status === 'NEW') {
                    message.success(t('auth.success'), 0.3).then(() => {
                        navigate(`/signup?target=${encodeURIComponent(redirect || '')}`)
                    });
                }
            })
    }, [authenticateUser, setWorking, navigate]);

    return {
        working,
        setWorking,
        login,
        captureUser
    }
}
